import React from 'react';
import styled from 'styled-components';
import { Mono } from '../../../../../../generic/ui/Mono';
import { Tag } from '../../../../../../generic/ui/Tag';
import { CUSTOMER_ID_COLOR } from './LikutSectionHeader';
import BoxesIcon from '../../../../../../generic/icons/IconLib/BoxesIcon';
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon';
import PalletIcon from '../../../../../../generic/icons/IconLib/PalletIcon';
import CoinIcon from '../../../../../../generic/icons/IconLib/CoinIcon';
import PhoneIcon from '../../../../../../generic/icons/IconLib/PhoneIcon';
import PencilIcon from '../../../../../../generic/icons/IconLib/PencilIcon';
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon';
import MessageSquareIcon from '../../../../../../generic/icons/IconLib/MessageSquareIcon';

interface Props {
    orderTitle: any;
    likutInfo: any;
    collectComm?: string;
    closeFunc?: (flag?: boolean) => void;
}

const ModalHeaderDetails: React.FC<Props> = ({ orderTitle, likutInfo, collectComm }) => {
    return (
        <Wrapper>
            {likutInfo?.clientName?.trim() && (
                <DetailRow>
                    <IconSlot>
                        <UserIcon color='currentColor' size={16} />
                    </IconSlot>
                    <TextValue>{likutInfo.clientName.trim()}</TextValue>
                    {likutInfo?.clientId != null && (
                        <Tag variant='tinted' color={CUSTOMER_ID_COLOR}>
                            {likutInfo.clientId}
                        </Tag>
                    )}
                </DetailRow>
            )}
            <DetailRow>
                <IconSlot>
                    <BoxesIcon color='currentColor' size={16} />
                </IconSlot>
                <Value>{orderTitle?.Pieces} / {orderTitle?.GivenPieces}</Value>
                <UnitLabel>יח</UnitLabel>
            </DetailRow>
            <DetailRow>
                <IconSlot>
                    <PackageBoxIcon color='currentColor' size={16} />
                </IconSlot>
                <Value>{orderTitle?.Packages} / {orderTitle?.GivenPackages}</Value>
            </DetailRow>
            <DetailRow>
                <IconSlot>
                    <ScaleIcon color='currentColor' size={16} />
                </IconSlot>
                <Value>{orderTitle?.Weight} / {orderTitle?.GivenWeight}</Value>
                <UnitLabel>ק"ג</UnitLabel>
            </DetailRow>
            {typeof likutInfo?.pallet?.palletDesc === 'string' && (
                <DetailRow>
                    <IconSlot>
                        <PalletIcon color='currentColor' size={16} />
                    </IconSlot>
                    <TextValue>{likutInfo.pallet.palletDesc}</TextValue>
                </DetailRow>
            )}
            {orderTitle?.Seller_Desc?.trim() && (
                <DetailRow>
                    <IconSlot>
                        <CoinIcon color='currentColor' size={16} />
                    </IconSlot>
                    <TextValue>{orderTitle.Seller_Desc.trim()}</TextValue>
                </DetailRow>
            )}
            {orderTitle?.Phone?.trim() && (
                <DetailRow>
                    <IconSlot>
                        <PhoneIcon color='currentColor' size={16} />
                    </IconSlot>
                    <Value>{orderTitle.Phone.trim()}</Value>
                </DetailRow>
            )}
            {orderTitle?.Remarks?.trim() && (
                <RemarkRow>
                    <IconSlot>
                        <PencilIcon color='currentColor' size={16} />
                    </IconSlot>
                    <RemarkText>{orderTitle.Remarks.trim()}</RemarkText>
                </RemarkRow>
            )}
            {collectComm?.trim() && (
                <CommentRow>
                    <IconSlot>
                        <MessageSquareIcon color='currentColor' size={16} />
                    </IconSlot>
                    <CommentText>{collectComm.trim()}</CommentText>
                </CommentRow>
            )}
        </Wrapper>
    );
};

export default ModalHeaderDetails;

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    direction: rtl;
    padding: 0.25rem 0.25rem 0.5rem;
`;

const DetailRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const RemarkRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.danger};
    border-radius: ${({ theme }) => theme.radii.md};
    color: ${({ theme }) => theme.colors.danger};
`;

const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;

    ${RemarkRow} & {
        color: ${({ theme }) => theme.colors.danger};
    }
`;

const Value = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const TextValue = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.sans};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const RemarkText = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    font-family: ${({ theme }) => theme.fonts.sans};
    color: ${({ theme }) => theme.colors.danger};
    white-space: pre-wrap;
    word-break: break-word;
`;

// Neutral note (collection comment) — unlike the danger-styled order remark.
const CommentRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.md};
    background: ${({ theme }) => theme.colors.bgLayout};
`;

const CommentText = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    font-family: ${({ theme }) => theme.fonts.sans};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: pre-wrap;
    word-break: break-word;
`;

const UnitLabel = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    font-family: ${({ theme }) => theme.fonts.sans};
`;
